/**
 * The document model: paragraphs of formatted runs, and the two-way bridge
 * between them and the contentEditable DOM the editor types into.
 *
 * A run is the smallest piece of text that shares one set of formatting — the
 * same unit Word itself stores — so what a person applies in the editor lands
 * on exactly that span of characters in the saved .docx.
 */

export interface Run {
  t: string;
  b?: 1;
  i?: 1;
  u?: 1;
  /** RRGGBB, no hash. */
  c?: string;
  /** points */
  sz?: number;
  f?: string;
}

export interface Para {
  runs: Run[];
  style?: string;
  align?: 'left' | 'center' | 'right' | 'justify';
}

export interface Doc {
  paragraphs: Para[];
  styles: string[];
}

export const DEFAULT_STYLES = ['Normal', 'Title', 'Heading 1', 'Heading 2', 'Heading 3', 'Quote'];

export function parseDoc(value: string): Doc | null {
  if (!value || !value.trimStart().startsWith('{')) return null;
  try {
    const parsed = JSON.parse(value);
    if (!parsed || !Array.isArray(parsed.paragraphs)) return null;
    return {
      paragraphs: parsed.paragraphs.map((p: any) => ({
        runs: (p.runs || []).map((r: any) => ({ ...r, t: r.t ?? '' })),
        style: p.style || 'Normal',
        align: p.align,
      })),
      styles: parsed.styles?.length ? parsed.styles : DEFAULT_STYLES,
    };
  } catch {
    return null;
  }
}

export function serialiseDoc(doc: Doc): string {
  return JSON.stringify({
    vault_doc: 1,
    styles: doc.styles,
    paragraphs: doc.paragraphs.map((p) => ({
      style: p.style || 'Normal',
      align: p.align,
      runs: p.runs.length ? p.runs : [{ t: '' }],
    })),
  });
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Render one paragraph's runs as the HTML the editor edits. */
export function runsToHtml(runs: Run[]): string {
  const html = runs
    .filter((r) => r.t !== '' || runs.length === 1)
    .map((r) => {
      const styles: string[] = [];
      if (r.b) styles.push('font-weight:700');
      if (r.i) styles.push('font-style:italic');
      if (r.u) styles.push('text-decoration:underline');
      if (r.c) styles.push(`color:#${r.c}`);
      if (r.sz) styles.push(`font-size:${r.sz}pt`);
      if (r.f) styles.push(`font-family:${r.f}`);
      const text = escapeHtml(r.t);
      return styles.length ? `<span style="${styles.join(';')}">${text}</span>` : text;
    })
    .join('');
  return html || '';
}

function toHex(colour: string): string | undefined {
  const m = /rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(colour);
  if (m) {
    const hex = [1, 2, 3]
      .map((i) => Number(m[i]).toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
    return hex === '000000' ? undefined : hex;
  }
  const h = /^#([0-9a-f]{6})$/i.exec(colour.trim());
  if (h) return h[1].toUpperCase() === '000000' ? undefined : h[1].toUpperCase();
  return undefined;
}

function ptFromCss(size: string): number | undefined {
  const px = /^([\d.]+)px$/.exec(size);
  if (px) return Math.round((Number(px[1]) * 0.75) * 10) / 10;
  const pt = /^([\d.]+)pt$/.exec(size);
  if (pt) return Math.round(Number(pt[1]) * 10) / 10;
  return undefined;
}

/**
 * Read a contentEditable paragraph back into runs.
 *
 * The browser is free to produce <b>, <span style>, <font> or nesting of all
 * three; every shape collapses to the same run list here, so what the person
 * sees is what the .docx receives.
 */
export function htmlToRuns(root: HTMLElement): Run[] {
  const runs: Run[] = [];

  const walk = (node: Node, inherited: Omit<Run, 't'>) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';
      if (!text) return;
      const last = runs[runs.length - 1];
      const same =
        last &&
        !!last.b === !!inherited.b &&
        !!last.i === !!inherited.i &&
        !!last.u === !!inherited.u &&
        last.c === inherited.c &&
        last.sz === inherited.sz &&
        last.f === inherited.f;
      if (same) last.t += text;
      else runs.push({ ...inherited, t: text });
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    if (el.tagName === 'BR') {
      const last = runs[runs.length - 1];
      if (last) last.t += ' ';
      return;
    }
    const next: Omit<Run, 't'> = { ...inherited };
    const tag = el.tagName;
    if (tag === 'B' || tag === 'STRONG') next.b = 1;
    if (tag === 'I' || tag === 'EM') next.i = 1;
    if (tag === 'U') next.u = 1;
    if (tag === 'FONT') {
      const colour = el.getAttribute('color');
      if (colour) {
        const hex = toHex(colour);
        if (hex) next.c = hex;
        else delete next.c;
      }
    }
    const style = el.style;
    if (style) {
      const weight = style.fontWeight;
      if (weight === 'bold' || Number(weight) >= 600) next.b = 1;
      else if (weight === 'normal' || Number(weight) === 400) delete next.b;
      if (style.fontStyle === 'italic') next.i = 1;
      else if (style.fontStyle === 'normal') delete next.i;
      if (style.textDecoration?.includes('underline') || style.textDecorationLine?.includes('underline')) next.u = 1;
      if (style.color) {
        const hex = toHex(style.color);
        if (hex) next.c = hex;
        else delete next.c;
      }
      if (style.fontSize) {
        const pt = ptFromCss(style.fontSize);
        if (pt) next.sz = pt;
      }
      if (style.fontFamily) next.f = style.fontFamily.replace(/['"]/g, '').split(',')[0].trim();
    }
    el.childNodes.forEach((child) => walk(child, next));
  };

  root.childNodes.forEach((child) => walk(child, {}));
  return runs.length ? runs : [{ t: '' }];
}

export const paraText = (p: Para) => p.runs.map((r) => r.t).join('');
